import React, { useState, useEffect } from 'react';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import Card from './Card';
import DailyLineChart from './DailyLineChart';
import PlatformPieChart from './PlatformPieChart';
import DevicePieChart from './DevicePieChart';
import BrowserBarChart from './BrowserBarChart';
import OsBarChart from './OsBarChart';
import TimeOfDayBarChart from './TimeOfDayBarChart';
import WorldMapChart from './WorldMapChart';
import CityMap from './CityMap';

function Dashboard({ onLogout }) {
  const [darkMode, setDarkMode] = useState(document.documentElement.classList.contains('dark'));
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [dailyError, setDailyError] = useState(false);
  const [platformData, setPlatformData] = useState(null);
  const [platformError, setPlatformError] = useState(false);
  const [deviceData, setDeviceData] = useState(null);
  const [deviceError, setDeviceError] = useState(false);
  const [browserData, setBrowserData] = useState(null);
  const [browserError, setBrowserError] = useState(false);
  const [osData, setOsData] = useState(null);
  const [osError, setOsError] = useState(false);
  const [timeData, setTimeData] = useState(null);
  const [timeError, setTimeError] = useState(false);
  const [countryData, setCountryData] = useState(null);
  const [countryError, setCountryError] = useState(false);
  const [cityData, setCityData] = useState(null);
  const [cityError, setCityError] = useState(false);

  useEffect(() => {
    // Sync dark mode class on html
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { 'Authorization': `Bearer ${token}` };

    // Fetch overview stats
    fetch(`${process.env.REACT_APP_API_BASE_URL}stats/overview`, { headers })
      .then(res => {
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error('Overview fetch failed');
        return res.json();
      })
      .then(data => {
        if (data) {
          setOverview(data);
        }
      })
      .catch(() => setOverviewError(true));

    // Fetch daily scan trend
    fetch(`${process.env.REACT_APP_API_BASE_URL}stats/daily`, { headers })
      .then(res => {
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error('Daily fetch failed');
        return res.json();
      })
      .then(data => {
        if (data) setDailyData(data);
      })
      .catch(() => setDailyError(true));

    // Fetch platform breakdown
    fetch(`${process.env.REACT_APP_API_BASE_URL}stats/platforms`, { headers })
      .then(res => {
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error('Platforms fetch failed');
        return res.json();
      })
      .then(data => {
        if (data) {
          // Convert data to array if needed
          let platforms = data;
          if (!Array.isArray(platforms)) {
            platforms = [];
            for (const key in data) {
              platforms.push({ platform: key.charAt(0).toUpperCase() + key.slice(1), count: data[key] });
            }
          } else {
            platforms = platforms.map(item => ({
              platform: item.platform || item.name || item.label || item.type || 'Unknown',
              count: item.count || item.value || 0
            }));
          }
          // Normalize platform names
          platforms = platforms.map(p => {
            const name = p.platform.toLowerCase();
            let label;
            if (name === 'ios') label = 'iOS';
            else if (name === 'android') label = 'Android';
            else label = name.charAt(0).toUpperCase() + name.slice(1);
            return { platform: label, count: p.count };
          });
          setPlatformData(platforms);
        }
      })
      .catch(() => setPlatformError(true));

    // Fetch device type breakdown
    fetch(`${process.env.REACT_APP_API_BASE_URL}stats/devices`, { headers })
      .then(res => {
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error('Devices fetch failed');
        return res.json();
      })
      .then(data => {
        if (data) {
          let devices = data;
          if (!Array.isArray(devices)) {
            devices = [];
            for (const key in data) {
              devices.push({ device: key.charAt(0).toUpperCase() + key.slice(1), count: data[key] });
            }
          } else {
            devices = devices.map(item => ({
              device: item.device || item.name || 'Unknown',
              count: item.count || item.value || 0
            }));
          }
          // Normalize device names
          devices = devices.map(d => {
            const name = d.device.toLowerCase();
            let label;
            if (name === 'mobile') label = 'Mobile';
            else if (name === 'tablet') label = 'Tablet';
            else if (name === 'desktop') label = 'Desktop';
            else label = name.charAt(0).toUpperCase() + name.slice(1);
            return { device: label, count: d.count };
          });
          setDeviceData(devices);
        }
      })
      .catch(() => setDeviceError(true));

    // Fetch browser distribution
    fetch(`${process.env.REACT_APP_API_BASE_URL}stats/browsers`, { headers })
      .then(res => {
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error('Browsers fetch failed');
        return res.json();
      })
      .then(data => {
        if (data) {
          let browsers = data;
          if (!Array.isArray(browsers)) {
            browsers = [];
            for (const key in data) {
              browsers.push({ browser: key, count: data[key] });
            }
          } else {
            browsers = browsers.map(item => ({
              browser: item.browser || item.name || item.label || 'Unknown',
              count: item.count || item.value || 0
            }));
          }
          // Capitalize browser names properly
          browsers = browsers.map(b => {
            const name = b.browser;
            let label = name;
            if (name.toLowerCase() === 'ie') label = 'IE';
            else label = name.charAt(0).toUpperCase() + name.slice(1);
            return { browser: label, count: b.count };
          });
          setBrowserData(browsers);
        }
      })
      .catch(() => setBrowserError(true));

    // Fetch OS distribution
    fetch(`${process.env.REACT_APP_API_BASE_URL}stats/os`, { headers })
      .then(res => {
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error('OS fetch failed');
        return res.json();
      })
      .then(data => {
        if (data) {
          let systems = data;
          if (!Array.isArray(systems)) {
            systems = [];
            for (const key in data) {
              systems.push({ os: key, count: data[key] });
            }
          } else {
            systems = systems.map(item => ({
              os: item.os || item.name || item.label || 'Unknown',
              count: item.count || item.value || 0
            }));
          }
          // Normalize OS names
          systems = systems.map(s => {
            const key = s.os.toLowerCase();
            let label;
            if (key === 'ios') label = 'iOS';
            else if (key === 'android') label = 'Android';
            else if (key === 'windows') label = 'Windows';
            else if (key === 'mac' || key === 'macos') label = 'macOS';
            else if (key === 'linux') label = 'Linux';
            else label = key.charAt(0).toUpperCase() + key.slice(1);
            return { os: label, count: s.count };
          });
          setOsData(systems);
        }
      })
      .catch(() => setOsError(true));

    // Fetch time-of-day distribution
    fetch(`${process.env.REACT_APP_API_BASE_URL}stats/timeOfDay`, { headers })
      .then(res => {
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error('TimeOfDay fetch failed');
        return res.json();
      })
      .then(data => {
        if (data) {
          let times = data;
          if (!Array.isArray(times)) {
            // Assume object with keys
            times = [
              { time: 'Morning', count: data.Morning || 0 },
              { time: 'Afternoon', count: data.Afternoon || 0 },
              { time: 'Evening', count: data.Evening || 0 },
              { time: 'Night', count: data.Night || 0 }
            ];
          } else {
            times = times.map(item => ({
              time: item.time || item.label || 'Time',
              count: item.count || item.value || 0
            }));
          }
          // Ensure standard order
          const order = ['Morning', 'Afternoon', 'Evening', 'Night'];
          times.sort((a, b) => order.indexOf(a.time) - order.indexOf(b.time));
          setTimeData(times);
        }
      })
      .catch(() => setTimeError(true));

    // Fetch country distribution for world map
    fetch(`${process.env.REACT_APP_API_BASE_URL}stats/countries`, { headers })
      .then(res => {
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error('Countries fetch failed');
        return res.json();
      })
      .then(data => {
        if (data) {
          let countries = data;
          if (!Array.isArray(countries)) {
            countries = [];
            for (const code in data) {
              countries.push({ country: code, count: data[code] });
            }
          } else {
            countries = countries.map(item => ({
              country: item.country || item.code || item.id || '??',
              count: item.count || item.value || 0
            }));
          }
          // Convert to format for WorldMap
          const formatted = countries.map(item => {
            return { country: item.country.toUpperCase(), value: item.count };
          });
          setCountryData(formatted);
        }
      })
      .catch(() => setCountryError(true));

    // Fetch city geolocation data
    fetch(`${process.env.REACT_APP_API_BASE_URL}stats/cities`, { headers })
      .then(res => {
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) throw new Error('Cities fetch failed');
        return res.json();
      })
      .then(data => {
        if (data) {
          let cities = data;
          if (!Array.isArray(cities)) {
            // Expect an array for city data
            cities = [];
            for (const key in data) {
              // if data keyed by city name with latlon inside? unclear, assume already array normally
              cities.push(data[key]);
            }
          }
          // Normalize city fields names
          cities = cities.map(item => ({
            city: item.city || item.name || 'Unknown',
            lat: item.lat || item.latitude || 0,
            lon: item.lon || item.lng || item.longitude || 0,
            count: item.count || item.value || 0
          }));
          setCityData(cities);
        }
      })
      .catch(() => setCityError(true));
  }, [onLogout]);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <img src="/logo-blue.png" alt="Whuups Logo" className="h-8 dark:hidden" />
          <img src="/logo-white.png" alt="Whuups Logo" className="h-8 hidden dark:block" />
          <span className="text-xl font-semibold ml-2">Analytics Dashboard</span>
        </div>
        <div className="flex items-center">
          <button onClick={toggleTheme} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
            {darkMode ? <SunIcon className="h-6 w-6 text-gray-800 dark:text-gray-100" /> : <MoonIcon className="h-6 w-6 text-gray-800 dark:text-gray-100" />}
          </button>
          <button onClick={onLogout} className="ml-4 px-4 py-2 bg-brand text-white font-medium rounded hover:opacity-90 transition-opacity">
            Logout
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 p-4">
        {/* Overview cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <Card title="Total Scans">
            {overviewError ? (
              <p className="text-sm text-gray-500">Data not available</p>
            ) : overview ? (
              <div className="text-3xl font-bold">{overview.totalScans}</div>
            ) : (
              <p className="text-sm text-gray-500">Loading...</p>
            )}
          </Card>
          <Card title="Unique Devices">
            {overviewError ? (
              <p className="text-sm text-gray-500">Data not available</p>
            ) : overview ? (
              <div className="text-3xl font-bold">{overview.uniqueDevices}</div>
            ) : (
              <p className="text-sm text-gray-500">Loading...</p>
            )}
          </Card>
        </div>
        {/* Daily trend line chart */}
        <Card title="Scan Trends Over Time" className="mb-4">
          {dailyError ? (
            <p className="text-sm text-gray-500">Unable to load chart</p>
          ) : dailyData ? (
            <DailyLineChart data={dailyData} />
          ) : (
            <p className="text-sm text-gray-500">Loading...</p>
          )}
        </Card>
        {/* Platform & Device breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Card title="Platform Breakdown">
            {platformError ? (
              <p className="text-sm text-gray-500">Unable to load chart</p>
            ) : platformData ? (
              <PlatformPieChart data={platformData} />
            ) : (
              <p className="text-sm text-gray-500">Loading...</p>
            )}
          </Card>
          <Card title="Device Type Breakdown">
            {deviceError ? (
              <p className="text-sm text-gray-500">Unable to load chart</p>
            ) : deviceData ? (
              <DevicePieChart data={deviceData} />
            ) : (
              <p className="text-sm text-gray-500">Loading...</p>
            )}
          </Card>
        </div>
        {/* Browser & OS distribution */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Card title="Browser Distribution">
            {browserError ? (
              <p className="text-sm text-gray-500">Unable to load chart</p>
            ) : browserData ? (
              <BrowserBarChart data={browserData} />
            ) : (
              <p className="text-sm text-gray-500">Loading...</p>
            )}
          </Card>
          <Card title="OS Distribution">
            {osError ? (
              <p className="text-sm text-gray-500">Unable to load chart</p>
            ) : osData ? (
              <OsBarChart data={osData} />
            ) : (
              <p className="text-sm text-gray-500">Loading...</p>
            )}
          </Card>
        </div>
        {/* Time of day distribution */}
        <Card title="Scans by Time of Day" className="mb-4">
          {timeError ? (
            <p className="text-sm text-gray-500">Unable to load chart</p>
          ) : timeData ? (
            <TimeOfDayBarChart data={timeData} />
          ) : (
            <p className="text-sm text-gray-500">Loading...</p>
          )}
        </Card>
        {/* World map and City map */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="World Map - Scans by Country">
            {countryError ? (
              <p className="text-sm text-gray-500">Unable to load map</p>
            ) : countryData ? (
              <WorldMapChart data={countryData} />
            ) : (
              <p className="text-sm text-gray-500">Loading...</p>
            )}
          </Card>
          <Card title="City Map - Scan Locations">
            {cityError ? (
              <p className="text-sm text-gray-500">Unable to load map</p>
            ) : cityData ? (
              <CityMap data={cityData} />
            ) : (
              <p className="text-sm text-gray-500">Loading...</p>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
